/**
 * Fits a fixed-size {@link ProgramBoard} into whatever screen it is being shown on.
 *
 * The board is laid out against a constant 1600x900 box; this scales that box with a CSS transform
 * until it just fits the space it is given, letterboxing the remainder. One transform — so the
 * proportions, type sizes and line breaks on a 4K wall screen are the SAME ones that were approved
 * on the trainer's laptop, and the same ones the PDF export captures.
 */

import React, { useEffect, useRef, useState } from 'react';
import { STAGE_HEIGHT, STAGE_WIDTH } from './ProgramBoard';

export const ProgramBoardStage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const hostRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;

        const measure = () => {
            const { width, height } = host.getBoundingClientRect();
            // A zero measurement means the host is not laid out yet (or we are in jsdom). Keeping
            // the previous scale shows the board at 1:1 rather than collapsing it to nothing.
            if (!width || !height) return;
            setScale(Math.min(width / STAGE_WIDTH, height / STAGE_HEIGHT));
        };

        measure();
        const observer =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        observer?.observe(host);
        // Entering fullscreen on a TV resizes the window without resizing the host in some
        // browsers, so both signals are needed.
        window.addEventListener('resize', measure);

        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);

    return (
        <div
            ref={hostRef}
            style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    width: STAGE_WIDTH,
                    height: STAGE_HEIGHT,
                    flexShrink: 0,
                    transform: `scale(${scale})`,
                    transformOrigin: 'center center',
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default ProgramBoardStage;
